function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { ButtonRoot, ContentWrapper, MainContentContainer, SubContentContainer, SubContentIconContainer, SpinnerContainer } from './styles';
import { Padding, SizeSemantics, Sizes, States, Types } from './constants';
import { Icon, Text, ThemingContext, InlineChildren } from '@doordash/design-language';
import { loadingSizeForButton, loadingStateForButton } from './utils';
import Loading from '@doordash/component-loading';
import PropTypes from 'prop-types';
import React from 'react';
import { getPropsToForward } from '@doordash/utilities';
import invariant from 'invariant';

var validateSubContentString = function validateSubContentString(input) {
  return typeof input === 'string' && input.length > 0 || typeof input === 'number';
};

var Button = function Button(_ref) {
  var onClick = _ref.onClick,
      size = _ref.size,
      padding = _ref.padding,
      isInline = _ref.isInline,
      isDepressed = _ref.isDepressed,
      isDisabled = _ref.isDisabled,
      isInRadioGroup = _ref.isInRadioGroup,
      isToggleable = _ref.isToggleable,
      isToggled = _ref.isToggled,
      isRaised = _ref.isRaised,
      children = _ref.children,
      leadingIcon = _ref.leadingIcon,
      trailingIcon = _ref.trailingIcon,
      leadingText = _ref.leadingText,
      trailingText = _ref.trailingText,
      state = _ref.state,
      type = _ref.type,
      id = _ref.id,
      tag = _ref.tag,
      onAnimationEnd = _ref.onAnimationEnd,
      accessibilityLabel = _ref.accessibilityLabel,
      accessibilityRole = _ref.accessibilityRole,
      accessibilityDescribedBy = _ref.accessibilityDescribedBy,
      href = _ref.href,
      target = _ref.target,
      props = _objectWithoutProperties(_ref, ["onClick", "size", "padding", "isInline", "isDepressed", "isDisabled", "isInRadioGroup", "isToggleable", "isToggled", "isRaised", "children", "leadingIcon", "trailingIcon", "leadingText", "trailingText", "state", "type", "id", "tag", "onAnimationEnd", "accessibilityLabel", "accessibilityRole", "accessibilityDescribedBy", "href", "target"]);

  var theme = React.useContext(ThemingContext);
  invariant(type !== Types.FlatPrimary && type !== Types.FlatSecondary && type !== Types.Plain ? padding !== Padding.None : true, "\n      Do not use Button.Padding.None with any type that is not\n      Button.Types.Plain or Button.Types.FlatPrimary or Button.Types.FlatSecondary.\n    ");
  invariant(type === Types.FlatPrimary || type === Types.FlatSecondary ? !isToggleable : true, "\n      Do not set isToggleable to true when type is\n      Button.Types.FlatPrimary or Button.Types.FlatSecondary.\n    ");
  invariant(isRaised ? type === Types.Plain || type === Types.Primary || type === Types.PrimaryPill : true, "\n      You can only use isRaised with Plain, Primary\n      or PrimaryPill type buttons.\n    ");
  invariant(type === Types.Link ? !validateSubContentString(leadingText) && !validateSubContentString(trailingText) : true, "\n      You cannot use leading/trailing text with when type is Button.Types.Link.\n    ");
  invariant(leadingIcon ? !validateSubContentString(leadingText) : true, "\n      You can not combine leadingIcon and leadingText, please only use one leading item at a time.\n    ");
  invariant(trailingIcon ? !validateSubContentString(trailingText) : true, "\n      You can not combine trailingIcon and trailingText, please only use one trailing item at a time.\n    ");
  React.useEffect(function () {
    if (size === Button.Sizes.XLarge) {
      // eslint-disable-next-line no-console
      console.warn('Warning! `XLarge` Button Size will be removed in Prism Web v7 - we recommend changing your size to `Large`');
    }
  }, [size]);
  React.useEffect(function () {
    if (type === Button.Types.PrimaryPill || type === Button.Types.SecondaryPill || type === Button.Types.TertiaryPill) {
      // eslint-disable-next-line no-console
      console.warn('Warning! `Pill` type Buttons will be removed in Prism Web v7 - we recommend changing your type and using `Shape` Theming for any shape customizations');
    }
  }, [type]);
  var labelStyles;
  var leadingTextStyles;
  var trailingTextStyles;
  var childSpacing;

  switch (size) {
    case Button.Sizes.Small:
      labelStyles = Text.Styles.ButtonSmallTitle;
      leadingTextStyles = Text.Styles.ButtonSmallLeading;
      trailingTextStyles = Text.Styles.ButtonSmallTrailing;
      childSpacing = InlineChildren.Sizes.XxSmall;
      break;
    // TODO (lindsey-menges): deprecate XLarge size in v7

    case Button.Sizes.Large:
    case Button.Sizes.XLarge:
      labelStyles = Text.Styles.ButtonLargeTitle;
      leadingTextStyles = Text.Styles.ButtonLargeLeading;
      trailingTextStyles = Text.Styles.ButtonLargeTrailing;
      childSpacing = InlineChildren.Sizes.Small;
      break;

    case Button.Sizes.Medium:
    default:
      labelStyles = Text.Styles.ButtonMediumTitle;
      leadingTextStyles = Text.Styles.ButtonMediumLeading;
      trailingTextStyles = Text.Styles.ButtonMediumTrailing;
      childSpacing = InlineChildren.Sizes.XSmall;
      break;
  }

  var hasLeadingOrTrailingContent = leadingIcon || trailingIcon || validateSubContentString(leadingText) || validateSubContentString(trailingText);
  var renderLeadingContainer = isInline ? leadingIcon || validateSubContentString(leadingText) : hasLeadingOrTrailingContent;
  var renderTrailingContainer = isInline ? trailingIcon || validateSubContentString(trailingText) : hasLeadingOrTrailingContent;
  var iconSize = size === Sizes.Small ? Icon.Sizes.Small : Icon.Sizes.Medium;
  var leadingTrailingContnentIsIconsOnly = (leadingIcon || trailingIcon) && !validateSubContentString(leadingText) && !validateSubContentString(trailingText);
  var propsToForward = getPropsToForward({
    props: props,
    prefixesToForward: ['data-', 'aria-']
  });
  return /*#__PURE__*/React.createElement(ButtonRoot, _extends({
    themeColors: theme.Colors,
    themeBorderRadius: theme.BorderRadius.ButtonDefault,
    themeBorderWidth: theme.BorderWidth,
    themeHeight: theme.Size[SizeSemantics[size]],
    shape: isToggleable ? theme.Shape.ButtonToggle : theme.Shape.ButtonDefault,
    onClick: isDisabled || state !== States.Default ? null : onClick,
    id: id,
    as: tag,
    size: size,
    type: "button",
    padding: padding,
    isRaised: isRaised,
    isInline: isInline,
    isDepressed: isDepressed,
    disabled: tag === 'button' ? isDisabled : undefined,
    kind: type,
    href: href,
    target: target,
    tabIndex: tag !== 'button' ? 0 : undefined,
    "aria-label": accessibilityLabel,
    "aria-pressed": isToggleable && !isInRadioGroup ? isToggled : undefined,
    "aria-disabled": tag !== 'button' ? isDisabled : undefined,
    "aria-describedby": accessibilityDescribedBy,
    isToggleable: isToggleable,
    isToggled: isToggled,
    role: typeof accessibilityRole !== 'undefined' ? accessibilityRole : tag !== 'button' ? 'button' : undefined
  }, propsToForward), state !== States.Default && /*#__PURE__*/React.createElement(SpinnerContainer, {
    type: type
  }, /*#__PURE__*/React.createElement(Loading, {
    size: Loading.Sizes[loadingSizeForButton(size)],
    color: Loading.Colors.CurrentColor,
    state: loadingStateForButton(state),
    onAnimationEnd: onAnimationEnd
  })), /*#__PURE__*/React.createElement(ContentWrapper, {
    isHidden: state !== States.Default,
    hasLeadingOrTrailingContent: hasLeadingOrTrailingContent,
    size: size,
    kind: type,
    padding: padding
  }, /*#__PURE__*/React.createElement(InlineChildren, {
    justifyContent: hasLeadingOrTrailingContent && !isInline ? InlineChildren.Justification.SpaceBetween : InlineChildren.Justification.Center,
    alignItems: InlineChildren.Alignment.Center,
    size: childSpacing
  }, renderLeadingContainer && /*#__PURE__*/React.createElement(SubContentContainer, {
    side: "leading",
    isInline: isInline,
    isOnlyIcons: leadingTrailingContnentIsIconsOnly,
    iconSize: iconSize
  }, leadingIcon && /*#__PURE__*/React.createElement(SubContentIconContainer, null, /*#__PURE__*/React.createElement(Icon, {
    type: leadingIcon,
    shouldAdjustSmallSizesWhitespace: true,
    size: iconSize,
    color: Icon.Colors.CurrentColor
  })), leadingText && !leadingIcon && /*#__PURE__*/React.createElement(Text, {
    overflow: Text.Overflows.Truncate,
    styles: leadingTextStyles,
    textAlign: Text.TextAlignments.Left,
    color: Text.Colors.CurrentColor
  }, leadingText)), /*#__PURE__*/React.createElement(MainContentContainer, {
    hasLeadingOrTrailingContent: hasLeadingOrTrailingContent
  }, /*#__PURE__*/React.createElement(Text, {
    overflow: Text.Overflows.Truncate,
    styles: labelStyles,
    textAlign: Text.TextAlignments.Center,
    color: Text.Colors.CurrentColor,
    textDecoration: type === Types.Link ? Text.TextDecorations.Underline : undefined
  }, children)), renderTrailingContainer && /*#__PURE__*/React.createElement(SubContentContainer, {
    side: "trailing",
    isInline: isInline,
    isOnlyIcons: leadingTrailingContnentIsIconsOnly,
    iconSize: iconSize
  }, trailingIcon && /*#__PURE__*/React.createElement(SubContentIconContainer, null, /*#__PURE__*/React.createElement(Icon, {
    type: trailingIcon,
    shouldAdjustSmallSizesWhitespace: true,
    size: iconSize,
    color: Icon.Colors.CurrentColor
  })), trailingText && !trailingIcon && /*#__PURE__*/React.createElement(Text, {
    overflow: Text.Overflows.Truncate,
    styles: trailingTextStyles,
    textAlign: Text.TextAlignments.Right,
    color: Text.Colors.CurrentColor
  }, trailingText)))));
};

Button.Padding = Padding;
Button.Sizes = Sizes;
Button.States = States;
Button.Types = Types;
Button.propTypes = {
  onClick: PropTypes.func,
  children: PropTypes.node.isRequired,
  leadingIcon: PropTypes.oneOf(Object.keys(Icon.Types)),
  trailingIcon: PropTypes.oneOf(Object.keys(Icon.Types)),
  leadingText: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  trailingText: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  padding: PropTypes.oneOf(Object.values(Button.Padding)),
  size: PropTypes.oneOf(Object.values(Button.Sizes)),
  state: PropTypes.oneOf(Object.values(Button.States)),
  type: PropTypes.oneOf(Object.values(Button.Types)),
  isRaised: PropTypes.bool,
  isInRadioGroup: PropTypes.bool,
  isDepressed: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isInline: PropTypes.bool,
  isToggleable: PropTypes.bool,
  isToggled: PropTypes.bool,
  onAnimationEnd: PropTypes.func,
  accessibilityLabel: PropTypes.string,
  accessibilityRole: PropTypes.string,
  accessibilityDescribedBy: PropTypes.string,
  id: PropTypes.string,
  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  href: PropTypes.string,
  target: PropTypes.string
};
Button.defaultProps = {
  children: 'Click Me',
  size: Button.Sizes.Medium,
  type: Button.Types.Primary,
  state: Button.States.Default,
  padding: Button.Padding.Default,
  isRaised: false,
  isDepressed: false,
  isDisabled: false,
  isInline: false,
  isToggleable: false,
  isToggled: false,
  tag: 'button'
};
export default Button;